import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import BackToCockpit from "@/components/BackToCockpit";
import { PublicGallery } from "@/components/PhotoGallery";
import {
  MapPin, ShieldCheck, IdCard, Plane, Award, Loader2, ArrowLeft, Send,
  Briefcase, Radio, Clock, User as UserIcon, FileCheck, ExternalLink, Zap,
  UserPlus, UserCheck, UserX, Users,
} from "lucide-react";

/**
 * /crew/:userId — Public-safe pilot profile with a DM button.
 * Read-only. Contact only via in-app DM. Requires auth.
 */
export default function CrewProfile() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [dmText, setDmText] = useState("");
  const [dmBusy, setDmBusy] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [buddyStatus, setBuddyStatus] = useState("none"); // none | pending_out | pending_in | accepted | self
  const [buddyBusy, setBuddyBusy] = useState(false);

  useEffect(() => {
    let ok = true;
    api.get(`/pilots/${userId}`)
      .then((r) => { if (ok) setP(r.data); })
      .catch((e) => { if (ok) setError(e?.response?.data?.detail || "Couldn't load profile"); });
    api.get(`/buddies/status/${userId}`)
      .then((r) => { if (ok) setBuddyStatus(r.data?.status || "none"); })
      .catch(() => {});
    return () => { ok = false; };
  }, [userId]);

  const buddyAction = async (verb) => {
    setBuddyBusy(true);
    try {
      if (verb === "add" || verb === "accept") {
        const endpoint = verb === "accept" ? `/buddies/accept/${userId}` : `/buddies/request/${userId}`;
        const r = await api.post(endpoint);
        setBuddyStatus(r.data?.status || (verb === "accept" ? "accepted" : "pending_out"));
        toast.success(r.data?.status === "accepted" ? "You're now Flying Buddies" : "Flying Buddy request sent");
      } else if (verb === "remove" || verb === "cancel") {
        await api.delete(`/buddies/${userId}`);
        setBuddyStatus("none");
        toast.success(verb === "cancel" ? "Request cancelled" : "Flying Buddy removed");
      } else if (verb === "decline") {
        await api.post(`/buddies/decline/${userId}`);
        setBuddyStatus("none");
        toast.success("Request declined");
      }
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    } finally {
      setBuddyBusy(false);
    }
  };

  const sendDm = async () => {
    const body = dmText.trim();
    if (!body) return;
    setDmBusy(true);
    try {
      await api.post(`/messages/with/${userId}`, { text: body });
      toast.success("Message sent");
      setDmText("");
      setDmOpen(false);
      // Deep-link into the inbox thread we just created.
      nav(`/inbox?user=${userId}`);
    } catch (e) {
      // 402/403 already pop the appropriate global modal; skip the redundant toast.
      const status = e?.response?.status;
      if (status !== 402 && status !== 403) {
        toast.error(errMsg(e, "Send failed"));
      }
    } finally {
      setDmBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <BackToCockpit />
        <div className="card !p-8 text-center">
          <div className="mono text-[10px] tracking-widest uppercase text-red-400">// LOAD ERROR</div>
          <div className="text-lg text-white mt-2">{error}</div>
          <Link to="/search" className="btn-hud mt-4 inline-flex"><ArrowLeft className="h-3.5 w-3.5" /> BACK TO SEARCH</Link>
        </div>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-cyan" />
      </div>
    );
  }

  const displayName = p.nickname ? `${p.nickname}` : `${p.first_name} ${p.last_name}`;
  const legalName = p.nickname ? `${p.first_name} ${p.last_name}` : null;
  const ratings = p.aircraft_types || [];
  const additional = p.additional_roles || [];
  const rp = p.role_profile || {};
  const photoUrl = p.id_photo_url || p.avatar_url;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <BackToCockpit label="BACK" />

      {/* Hero */}
      <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start">
        <div className="relative">
          <div
            className="aspect-square w-full rounded-xl overflow-hidden border border-white/10 relative"
            style={{ background: "linear-gradient(160deg, rgba(75,156,255,0.10), rgba(5,8,16,0.85))" }}
          >
            {photoUrl ? (
              <img
                data-testid="crew-photo"
                src={photoUrl}
                alt={displayName}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/40">
                <UserIcon className="h-16 w-16" />
              </div>
            )}
            {p.faa_verified && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 mono text-[9px] tracking-widest uppercase px-2 py-1 rounded-full bg-avail/20 border border-avail/50 text-avail">
                <ShieldCheck className="h-3 w-3" /> FAA VERIFIED
              </div>
            )}
          </div>
          {p.digital_id_public_token && (
            <a
              href={`/id/${p.digital_id_public_token}`}
              target="_blank"
              rel="noreferrer"
              data-testid="crew-view-badge"
              className="mt-2 mono text-[10px] tracking-widest uppercase w-full inline-flex justify-center gap-1 px-3 py-1.5 rounded border border-cyan/40 text-cyan hover:bg-cyan/10"
            >
              <IdCard className="h-3 w-3" /> VIEW LIVE BADGE <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>

        <div className="min-w-0">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// CREW · PROFILE</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-none mt-1" data-testid="crew-name">{displayName}</h1>
          {legalName && (
            <div className="text-[13px] text-white/50 mt-1">Legal name: {legalName}</div>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-[12px] text-white/70">
            {(p.location_icao || p.location) && (
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-cyan" /> {p.location_icao || p.location}</span>
            )}
            {p.pjp_member_number != null && (
              <span className="mono text-[11px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-white/15 text-white/70">
                PJP-ID {String(p.pjp_member_number).padStart(5, "0")}
              </span>
            )}
            {p.available_to_work && (
              <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-avail/15 text-avail border border-avail/40 flex items-center gap-1">
                <Briefcase className="h-2.5 w-2.5" /> AVAILABLE
              </span>
            )}
            {p.share_live_location && (
              <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-cyan/15 text-cyan border border-cyan/40 flex items-center gap-1">
                <Radio className="h-2.5 w-2.5" /> LIVE LOC
              </span>
            )}
            {p.last_seen_airport && (
              <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-white/[0.03] border border-white/10 text-white/60 flex items-center gap-1">
                <Zap className="h-2.5 w-2.5" /> Last seen {p.last_seen_airport.icao}
              </span>
            )}
            {["pilot","flight_attendant","amt","mechanic"].includes(p.role) &&
              ["pilot","flight_attendant","amt"].includes(p.membership_tier_slug) &&
              p.empty_leg_crew_active && (
              <span
                data-testid="crew-empty-leg-perk"
                title="PJP crew perk — Free JumpSeat access included with paid membership"
                className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full flex items-center gap-1"
                style={{ background: "rgba(46,213,115,0.14)", color: "#5FE895", border: "1px solid rgba(46,213,115,0.45)" }}
              >
                <Plane className="h-2.5 w-2.5" /> JUMPSEAT · FREE ACCESS
              </span>
            )}
          </div>

          {/* DM CTA */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDmOpen((v) => !v)}
              data-testid="crew-dm-toggle"
              className="mono text-[10px] tracking-widest uppercase px-4 py-2 rounded bg-cyan text-obsidian font-bold hover:brightness-110 flex items-center gap-1.5"
            >
              <Send className="h-3.5 w-3.5" /> SEND DM
            </button>
            <Link
              to={`/inbox?user=${p.id}`}
              data-testid="crew-open-thread"
              className="mono text-[10px] tracking-widest uppercase px-4 py-2 rounded border border-white/20 text-white/80 hover:bg-white/5 flex items-center gap-1.5"
            >
              Open thread
            </Link>
            {buddyStatus !== "self" && (
              <BuddyButton status={buddyStatus} busy={buddyBusy} onAction={buddyAction} />
            )}
          </div>

          {dmOpen && (
            <div className="mt-3 rounded-lg border border-cyan/30 bg-cyan/[0.06] p-3" data-testid="crew-dm-compose">
              <textarea
                data-testid="crew-dm-body"
                value={dmText}
                onChange={(e) => setDmText(e.target.value)}
                placeholder={`Message ${displayName}…`}
                rows={3}
                className="input w-full !bg-obsidian/60"
              />
              <div className="flex items-center justify-end gap-2 mt-2">
                <button onClick={() => setDmOpen(false)} className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-white/15 text-white/60 hover:bg-white/5">
                  CANCEL
                </button>
                <button
                  onClick={sendDm}
                  data-testid="crew-dm-send"
                  disabled={dmBusy || !dmText.trim()}
                  className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded bg-cyan text-obsidian font-bold disabled:opacity-40 flex items-center gap-1.5"
                >
                  {dmBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} SEND
                </button>
              </div>
            </div>
          )}

          {p.bio && (
            <div className="mt-5 text-[13px] text-white/70 leading-relaxed max-w-2xl whitespace-pre-line">{p.bio}</div>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <Stat icon={Clock} label="Total hours" value={(p.total_hours || 0).toLocaleString()} />
        <Stat icon={Plane} label="Type ratings" value={ratings.length} />
        <Stat icon={FileCheck} label="Verified docs" value={p.verified_document_count ?? 0} />
        <Stat icon={Zap} label="Badge flashes" value={p.flashes_total ?? 0} />
      </div>

      {/* Photo gallery — read-only for visitors */}
      <PublicGallery photos={p.photos || []} />

      {/* Type ratings */}
      {ratings.length > 0 && (
        <Section title="Type Ratings" overline={`// ${ratings.length} rated`}>
          <div className="flex flex-wrap gap-1.5">
            {ratings.map((t) => (
              <span
                key={t}
                data-testid={`crew-rating-${t}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full mono text-[11px] tracking-widest uppercase border border-cyan/40 bg-cyan/10 text-cyan"
              >{t}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Certs & seats */}
      <Section title="Identity & Certificates" overline="// FAA">
        <div className="grid md:grid-cols-2 gap-3">
          <Fact k="Pilot cert #" v={p.pilot_certificate_number_masked || "—"} />
          <Fact k="Preferred seat" v={rp.preferred_seat || "—"} />
          <Fact k="Home base" v={p.location_icao || p.location || "—"} />
          <Fact k="Medical class" v={rp.medical_class || rp.medical || "—"} />
          {additional.length > 0 && (
            <Fact k="Also hats" v={additional.join(" · ")} />
          )}
          {p.last_seen_airport?.icao && (
            <Fact k="Last badge flash" v={`${p.last_seen_airport.icao} · ${p.last_seen_airport.city || ""}`} />
          )}
        </div>
      </Section>

      {/* Role-specific bits (freeform) */}
      {rp && Object.keys(rp).length > 0 && (
        <Section title="Role Profile" overline="// DEEPER">
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(rp)
              .filter(([k, v]) => v !== null && v !== undefined && v !== "" && !["preferred_seat", "medical_class", "medical"].includes(k))
              .slice(0, 10)
              .map(([k, v]) => (
                <Fact key={k} k={k.replaceAll("_", " ")} v={typeof v === "object" ? JSON.stringify(v) : String(v)} />
              ))}
          </div>
        </Section>
      )}

      {/* Bottom CTA */}
      <div className="mt-10 rounded-lg border border-cyan/30 p-4 bg-cyan/[0.06] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-white/75">
          <Award className="h-4 w-4 text-cyan" /> Reach out and coordinate directly with {displayName}
        </div>
        <button
          type="button"
          onClick={() => setDmOpen(true)}
          data-testid="crew-dm-bottom"
          className="mono text-[10px] tracking-widest uppercase px-4 py-2 rounded bg-cyan text-obsidian font-bold hover:brightness-110 flex items-center gap-1.5"
        >
          <Send className="h-3.5 w-3.5" /> MESSAGE {displayName.split(" ")[0].toUpperCase()}
        </button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="mono text-[9px] tracking-widest uppercase text-white/40 flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mono text-2xl font-bold text-cyan mt-1">{value}</div>
    </div>
  );
}

function Section({ title, overline, children }) {
  return (
    <div className="mt-8">
      <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">{overline}</div>
      <div className="font-display text-xl md:text-2xl font-bold text-white tracking-tight mt-0.5 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Fact({ k, v }) {
  return (
    <div className="rounded border border-white/[0.06] bg-white/[0.02] p-2.5 flex items-baseline justify-between gap-3">
      <div className="mono text-[10px] tracking-widest uppercase text-white/45 capitalize">{k}</div>
      <div className="mono text-[12px] text-white/85 text-right break-all">{v}</div>
    </div>
  );
}

/**
 * BuddyButton renders the right CTA based on the current buddy status:
 *   none         -> ADD FLYING BUDDY
 *   pending_out  -> REQUEST SENT (click to cancel)
 *   pending_in   -> ACCEPT REQUEST (+ decline)
 *   accepted     -> BUDDIES ✓ (click to remove)
 */
function BuddyButton({ status, busy, onAction }) {
  const btn = "mono text-[10px] tracking-widest uppercase px-4 py-2 rounded flex items-center gap-1.5 disabled:opacity-40";
  if (status === "accepted") {
    return (
      <button
        type="button"
        onClick={() => onAction("remove")}
        disabled={busy}
        data-testid="crew-buddy-remove"
        title="Remove Flying Buddy"
        className={`${btn} border border-avail/50 bg-avail/[0.10] text-avail hover:bg-avail/[0.18]`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} FLYING BUDDIES
      </button>
    );
  }
  if (status === "pending_out") {
    return (
      <button
        type="button"
        onClick={() => onAction("cancel")}
        disabled={busy}
        data-testid="crew-buddy-cancel"
        title="Cancel request"
        className={`${btn} border border-white/20 text-white/70 hover:bg-white/5`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />} REQUEST SENT
      </button>
    );
  }
  if (status === "pending_in") {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onAction("accept")}
          disabled={busy}
          data-testid="crew-buddy-accept"
          className={`${btn} bg-avail text-obsidian font-bold hover:brightness-110`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} ACCEPT FLYING BUDDY
        </button>
        <button
          type="button"
          onClick={() => onAction("decline")}
          disabled={busy}
          data-testid="crew-buddy-decline"
          className={`${btn} border border-white/20 text-white/70 hover:bg-white/5`}
        >
          <UserX className="h-3.5 w-3.5" /> DECLINE
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onAction("add")}
      disabled={busy}
      data-testid="crew-buddy-add"
      className={`${btn} border border-cyan/50 bg-cyan/[0.10] text-cyan hover:bg-cyan/[0.18]`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} ADD FLYING BUDDY
    </button>
  );
}
